import { FileText, LayoutTemplate, GitBranch, Sparkles, Building2, FileCheck2 } from 'lucide-react';
import { Button } from '../components/ui/button';
import type { Go } from './types';

const SECTIONS = [
  {
    icon: FileText,
    title: 'Masters & project specs',
    body: 'Keep your firm’s gold-standard sections as living masters with a core-and-variant model. Compile them into a project, then tailor only what changes — no copy-paste drift.',
  },
  {
    icon: LayoutTemplate,
    title: 'The Forma formatting engine',
    body: 'Define headers, footers, numbering, margins, and tokens once. Forma resolves project and issuance data automatically and exports pixel-accurate Word documents every time.',
  },
  {
    icon: GitBranch,
    title: 'Draft, review, issue, amend',
    body: 'A git-like workflow built for A/E delivery. Draft safely, route for review, publish, and track issuances and addenda with baseline snapshots and change tracking.',
  },
  {
    icon: Sparkles,
    title: 'One master, tailored per client',
    body: 'Layer each client’s own wording on top of the firm’s master, without ever forking the section. The master stays the single source of truth, and every client’s version is one click from a clean Word export.',
  },
  {
    icon: Building2,
    title: 'Teams, roles & firms',
    body: 'Organizations, teams, and fine-grained permissions, with strict per-firm isolation. Your masters and standards stay yours, full stop.',
  },
  {
    icon: FileCheck2,
    title: 'Issue-ready project manuals',
    body: 'Compile a complete project manual with correct pagination and intentional blank-page parity — ready to issue, on the web or the desktop app.',
  },
];

export function FeaturesPage({ go }: { go: Go }) {
  return (
    <div className="mx-auto max-w-6xl px-4 py-20 sm:px-6">
      <div className="mx-auto max-w-2xl text-center">
        <h1 className="text-4xl font-bold tracking-tight text-text">
          Built for how specs really get written
        </h1>
        <p className="mt-4 text-lg text-muted">
          Every part of SpecForma is shaped around the daily reality of architecture and engineering
          specification work.
        </p>
      </div>

      <div className="mt-16 grid gap-10 md:grid-cols-2">
        {SECTIONS.map(({ icon: Icon, title, body }) => (
          <div key={title} className="flex gap-4">
            <span className="flex size-11 shrink-0 items-center justify-center rounded-lg bg-primary-soft text-primary">
              <Icon className="size-5" aria-hidden="true" />
            </span>
            <div>
              <h2 className="text-xl font-semibold text-text">{title}</h2>
              <p className="mt-2 text-md leading-relaxed text-muted">{body}</p>
            </div>
          </div>
        ))}
      </div>

      <div className="mt-16 flex flex-col items-center gap-3 text-center">
        <Button size="lg" onClick={() => go('auth')}>
          Get started
        </Button>
        <button
          type="button"
          onClick={() => go('pricing')}
          className="rounded text-md font-medium text-primary hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary"
        >
          Or see pricing
        </button>
      </div>
    </div>
  );
}
